import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateOption, Option, UpdateOption } from '@thro-types/leihbar';

import { createOption, deleteOption, getOptionsForProduct, updateOption } from 'api';
import { AxiosRequestConfig } from 'axios';

/**
 * A custom hook for Options
 *
 * @returns DefinedUseQueryResult<Option[], Error>
 */
export const useOptionsForProductQuery = (productId: number, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['options', productId],
    queryFn: () => {
      return getOptionsForProduct(productId, config);
    }
  });

  return query;
};

export const useOptionMutation = (productId: number, config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['options', productId];

  const create = useMutation<Option, Error, CreateOption>({
    mutationFn: async args => {
      return await createOption(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
    onError: data => {}
  });

  const update = useMutation<Option, Error, { id: number; data: UpdateOption }>({
    mutationFn: async args => {
      return await updateOption(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
    onError: data => {}
  });

  const deletion = useMutation<Option, Error, number>({
    mutationFn: async args => {
      return await deleteOption({ id: args, productId }, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
